import React, { useMemo } from 'react';
import cn from 'classnames';
import { TreatmentType } from 'libs/end-to-end/src/treatment';
import { TreatmentTypeNativeEnum } from 'libs/end-to-end/src/treatment';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TREATMENT_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { ImpactFactor } from 'libs/end-to-end/src/impact-factor';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface RecommendedSolutionCardProps {
  treatmentType: TreatmentType;
  impactFactor: ImpactFactor;
  onClick: () => void;
}

/**
 * @note these classnames have underscores instead of spaces and no spaces after the commas so they work with tailwind
 */
// eslint-disable-next-line complexity
const getStylesForRecommendedSolutionVariant = (
  treatmentType: TreatmentType,
): {
  idleGradient: Nullable<`bg-${string}`>;
  hoveredGradient: Nullable<`hover:bg-${string}`>;
} => {
  switch (treatmentType) {
    case TreatmentTypeNativeEnum.DiversityCopilot:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F7F4E9_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F9E473_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.ManagerUpskilling:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F2FAF0_0%,#FFFFFF_100%)',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#A4EB70_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.CompensationCopilot:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F0FAF8_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#75EBCD_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.HiringProcessOptimization:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FFF5F5_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FCCCCC_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.SuccessPhenotype:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F5F6FA_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#CAD6F8_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.ManagerBestPractices:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FFF5FA_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FBC9E2_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.TrainingStrategyCounterStereotype:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F7F5FC_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#DAD0FA_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.HiringCopilot:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F9F5FF_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#E5CFFA_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.PerformanceProcessOptimization:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F5F6FA_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#CAD6F8_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.TrainingStrategyEmpathySkills:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F7F4E9_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F9E473_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.IntelligentSourcingStrategy:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F5F5FF_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#DAD0FA_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.TrainingStrategyStructuralInequality:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FFF4F0_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#FACBBB_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.PromotionCopilot:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F0F7FF_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#BCDAF8_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.PromotionProcessOptimization:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F9F5FF_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#E5CFFA_0%,#FFFFFF_100%)]',
      };
    case TreatmentTypeNativeEnum.SourcingCopilot:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F2FAF0_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#A6ED8E_0%,#FFFFFF_100%)]',
      };

    default:
      return {
        idleGradient:
          'bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#F7F5FC_0%,#FFFFFF_100%)]',
        hoveredGradient:
          'hover:bg-[radial-gradient(99.22%_228.16%_at_99.22%_97.97%,#DAD0FA_0%,#FFFFFF_100%)]',
      };
  }
};

const ImpactFactorPill: React.FC<{ impactFactor: ImpactFactor }> = ({
  impactFactor,
}) => {
  const baseStyles = 'rounded-full py-1 px-2';
  const language = useLanguage();

  switch (impactFactor) {
    case ImpactFactor.Values.Low:
      return (
        <span className={cn(baseStyles, 'bg-gray-200')}>
          <S2Typography
            variant="headline-7-alt"
            translatedContent={language.recommendedSolutionImpactFactorPillText(
              { impactFactor: 'Low' },
            )}
            textColorVariant="gray-800"
          />
        </span>
      );
    case ImpactFactor.Values.Medium:
      return (
        <span className={cn(baseStyles, 'bg-yellow-200')}>
          <S2Typography
            variant="headline-7-alt"
            translatedContent={language.recommendedSolutionImpactFactorPillText(
              {
                impactFactor: 'Medium',
              },
            )}
            textColorVariant="yellow-700"
          />
        </span>
      );
    case ImpactFactor.Values.High:
      return (
        <span className={cn(baseStyles, 'bg-green-200')}>
          <S2Typography
            variant="headline-7-alt"
            translatedContent={language.recommendedSolutionImpactFactorPillText(
              { impactFactor: 'High' },
            )}
            textColorVariant="teal-700"
          />
        </span>
      );
  }
};

const deprecatedTreatmentTypes = new Set<Partial<TreatmentType>>([
  TreatmentTypeNativeEnum.deprecated_InterGroupContact,
  TreatmentTypeNativeEnum.deprecated_InterGroupContactTraining,
  TreatmentTypeNativeEnum.deprecated_BestPracticesMakingAlgorithms,
  TreatmentTypeNativeEnum.ProvideMoreData,
]);

const treatmentTypeHasIcon = (treatmentType: TreatmentType): boolean => {
  return !deprecatedTreatmentTypes.has(treatmentType);
};

const RecommendedSolutionCardText: React.FC<
  Pick<RecommendedSolutionCardProps, 'treatmentType' | 'impactFactor'>
> = ({ treatmentType, impactFactor }) => {
  const language = useLanguage();

  return (
    <Spacing
      direction="vertical"
      size="lg"
      className="items-start min-h-[6rem]"
    >
      <Spacing
        direction="vertical"
        size="xs"
        className="justify-between items-start text-left"
      >
        <S2Typography
          variant="small-label"
          translatedContent={language.recommendedSolutionHeader()}
          textColorVariant="gray-900"
        />

        <S2Typography
          variant="headline-6-alt"
          translatedContent={language[TREATMENT_MESSAGE_IDS[treatmentType]]()}
        />
      </Spacing>

      <Spacing
        direction="horizontal"
        size="sm"
        className="items-center absolute bottom-3"
      >
        <S2Typography
          variant="body-7"
          translatedContent={language.recommendedSolutionImpact()}
          textColorVariant="gray-900"
        />

        <ImpactFactorPill impactFactor={impactFactor} />
      </Spacing>
    </Spacing>
  );
};

export const RecommendedSolutionCard: React.FC<
  RecommendedSolutionCardProps
> = ({ treatmentType, impactFactor, onClick }) => {
  const styles = useMemo(
    () => getStylesForRecommendedSolutionVariant(treatmentType),
    [treatmentType],
  );

  return (
    <button onClick={onClick} className="cursor-pointer w-full relative">
      <Card
        backgroundClassName={
          styles.idleGradient
            ? `${styles.idleGradient} ${styles.hoveredGradient}`
            : undefined
        }
        shadowClassName="shadow-2px hover:shadow-4px "
        className="hover:-translate-y-1"
      >
        <Spacing
          direction="horizontal"
          size="sm"
          className="justify-between items-center"
        >
          <RecommendedSolutionCardText
            impactFactor={impactFactor}
            treatmentType={treatmentType}
          />

          {treatmentTypeHasIcon(treatmentType) && (
            <img
              src={`/images/recommended-solutions-icons/${treatmentType}.png`}
              // Decorative icon only, alt not helpful
              alt=""
              className="w-[96px] h-[96px]"
            />
          )}
        </Spacing>
      </Card>
    </button>
  );
};
